import { Dispatch, startTransition, useEffect, useRef, useState } from "react";
import { Form, FormControlProps } from "react-bootstrap";
import { valueFix } from "../utility";
import "./ControlBase.scss";

/**
 * Props for the ControlBase component.
 */
export interface ControlBaseProps extends FormControlProps {
  allowCarriageReturn?: boolean;
  autoFocus?: boolean;
  className?: string;
  debounceMilliseconds?: number;
  disabled?: boolean;
  forcePrefix?: string;
  placeholder?: string;
  rows?: number;
  setValue?: Dispatch<string>;
  spellCheck?: boolean;
  type?: "string" | "textarea" | "number";
  value: string;
  whiteSpaceFix?: boolean;
}

/**
 * A basic input control component that provides common input functionality with customizable types and styling.
 *
 * @param props - Component props.
 * @param props.allowCarriageReturn - Whether to allow carriage returns in textarea (defaults to false)
 * @param props.autoFocus - Whether the input should automatically receive focus
 * @param props.className - Additional CSS class names to apply (defaults to "")
 * @param props.debounceMilliseconds - Debounce delay in milliseconds
 *        0 for immediate updates (defaults to 500)
 *        <0 for no debounce (updates only on blur)
 * @param props.disabled - Whether the control is disabled (defaults to false)
 * @param props.forcePrefix - A prefix that must be present at the start of the value (defaults to "")
 * @param props.placeholder - Placeholder text to display when input is empty
 * @param props.rows - Number of rows for textarea type
 * @param props.setValue - Callback function to update the value when changed
 * @param props.spellCheck - Whether spell checking is enabled (defaults to true)
 * @param props.type - The type of input control to render (defaults to "string")
 * @param props.value - The current value of the input control
 * @param props.whiteSpaceFix - Whether to automatically trim whitespace on blur (defaults to false)
 * @param props.rest - Additional FormControlProps from React Bootstrap
 * @returns A React functional component rendering a Form.Control
 */
export const ControlBase = (props: ControlBaseProps): React.ReactElement => {
  const {
    allowCarriageReturn = false,
    className = "",
    debounceMilliseconds = 500,
    disabled = false,
    forcePrefix = "",
    setValue,
    spellCheck = true,
    type = "string",
    value,
    whiteSpaceFix = false,
    ...rest
  } = props;

  const [currentValue, setCurrentValue] = useState(value);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Force an immediate update, bypassing debounce.
   *
   * @param newValue - Optional new value to set before forcing the update
   */
  const forceUpdate = (newValue?: string) => {
    // Clear any pending debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }

    // Use provided value or current value
    const valueToUpdate = newValue ?? currentValue;

    // Update parent if setValue is available
    setValue?.(valueToUpdate);

    // Update local state if new value provided
    if (newValue !== undefined) {
      setCurrentValue(valueToUpdate);
    }
  };

  // Sync with parent value changes
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    startTransition(() => setCurrentValue(value));
  }, [value]);

  // Handle debounced updates when currentValue changes
  useEffect(() => {
    const fixedValue = whiteSpaceFix ? valueFix(value, true) : value;
    // Only debounce if setValue exists and value differs from parent
    /* istanbul ignore else */
    if (setValue && currentValue !== fixedValue) {
      // Clear existing timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set up debounced update (0ms for immediate, for negative values no need to set)
      if (debounceMilliseconds >= 0) {
        debounceTimer.current = setTimeout(() => {
          setValue(currentValue);
          debounceTimer.current = null;
        }, debounceMilliseconds);
      }
    } else if (currentValue === fixedValue) {
      // If values are the same, ensure no pending timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    }

    // Cleanup function to clear timer on unmount
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [currentValue, debounceMilliseconds, setValue, value, whiteSpaceFix]);

  return (
    <Form.Control
      {...rest}
      autoFocus={props.autoFocus}
      as={type === "textarea" ? "textarea" : undefined}
      className={className}
      disabled={disabled || !setValue}
      onBlur={(e) => {
        if (e.currentTarget.value !== value) {
          const fixedValue = whiteSpaceFix
            ? valueFix(e.currentTarget.value, true)
            : e.currentTarget.value;
          setCurrentValue(fixedValue);
          forceUpdate(fixedValue);
        }
      }}
      onChange={(e) => {
        e.preventDefault();
        e.stopPropagation();
        /* istanbul ignore else */
        if (e.currentTarget.value.startsWith(forcePrefix)) {
          setCurrentValue(e.currentTarget.value);
        }
      }}
      onKeyDown={(e) => {
        if (
          (e.key === "Enter" && !allowCarriageReturn) ||
          (e.key === "Enter" && e.shiftKey && allowCarriageReturn && type === "textarea")
        ) {
          e.preventDefault();
          e.stopPropagation();
          forceUpdate();
        }
      }}
      placeholder={props.placeholder}
      size="sm"
      spellCheck={spellCheck}
      type={type !== "textarea" ? type : undefined}
      value={currentValue}
    />
  );
};

ControlBase.displayName = "ControlBase";
